/**
 * templates/categories/series.js - Series Detail Page Templates
 * Location: /app/frontend/src/admin/page_builder/templates/categories/
 * 
 * Templates for rendering series detail pages (comic and novel).
 * Uses a hybrid approach: Page Builder provides shell, existing components render in slots.
 */

/**
 * Series Hero - Top section with cover, title, creator info
 * Uses data from the series being viewed (dynamic)
 */
export const series_hero = {
  id: 'series_hero',
  name: 'Series Hero',
  description: 'Dynamic hero section for series detail pages with cover art, title, and creator info',
  category: 'series',
  blockType: 'section_series_hero',
  icon: 'book-open',
  // Constraints
  constraints: {
    allowedLayouts: ['overlay', 'split'],
    maxBlocks: 1,
    requiredFields: [],
  },
  // Default props - populated dynamically from series data
  defaultProps: {
    heading: '{{series.title}}',
    subheading: 'by {{series.creator_name}}',
    showCover: true,
    showStats: true,
    showSubscribeButton: true,
    showShareButton: true,
    showTags: true,
  },
  layout: {
    preset: 'overlay',
    containerWidth: 'full',
    padding: { top: 0, bottom: 0 },
  },
  background: {
    type: 'image',
    imageUrl: '{{series.cover_url}}',
    overlay: { type: 'gradient', direction: 'to-t', from: 'rgba(0,0,0,0.95)', to: 'rgba(0,0,0,0.3)' },
  },
};

/**
 * Episode List - Chapter/episode listing with sorting
 * Core content section - renders the episode list component
 */
export const episode_list = {
  id: 'episode_list',
  name: 'Episode List',
  description: 'Sortable list of episodes/chapters with thumbnails and reading progress',
  category: 'series',
  blockType: 'section_episode_list',
  icon: 'list',
  // Constraints
  constraints: {
    allowedLayouts: ['single'],
    maxBlocks: 1,
    requiredFields: [],
  },
  // Default props
  defaultProps: {
    heading: 'Episodes',
    showThumbnails: true,
    showProgress: true,
    defaultSort: 'newest',
    allowSort: true,
    showStatus: true,
    showPublishDate: true,
  },
  layout: {
    preset: 'single',
    containerWidth: 'contained',
    padding: { top: 32, bottom: 32 },
  },
  background: {
    type: 'solid',
    color: 'transparent',
  },
};

/**
 * Series Info Panel - Description, stats, tags
 * Sidebar/below fold section with series metadata
 */
export const series_info_panel = {
  id: 'series_info_panel',
  name: 'Series Info Panel',
  description: 'Series description, tags, and detailed statistics',
  category: 'series',
  blockType: 'section_series_info',
  icon: 'info',
  // Constraints
  constraints: {
    allowedLayouts: ['single', 'split'],
    maxBlocks: 1,
    requiredFields: [],
  },
  // Default props
  defaultProps: {
    showDescription: true,
    showTags: true,
    showStats: true,
    showSchedule: true,
    showRating: true,
  },
  layout: {
    preset: 'single',
    containerWidth: 'contained',
    padding: { top: 24, bottom: 24 },
  },
  background: {
    type: 'solid',
    color: 'var(--color-surface-secondary)',
  },
};

/**
 * Series Comments - Comment section with reactions
 */
export const series_comments = {
  id: 'series_comments',
  name: 'Series Comments',
  description: 'User comments and reactions for the series',
  category: 'series',
  blockType: 'section_series_comments',
  icon: 'message-circle',
  // Constraints
  constraints: {
    allowedLayouts: ['single'],
    maxBlocks: 1,
    requiredFields: [],
  },
  // Default props
  defaultProps: {
    heading: 'Comments',
    showReactions: true,
    allowComments: true,
    sortDefault: 'newest',
    limit: 20,
  },
  layout: {
    preset: 'single',
    containerWidth: 'contained',
    padding: { top: 24, bottom: 24 },
  },
  background: {
    type: 'solid',
    color: 'transparent',
  },
};

/**
 * Related Series - Similar/recommended series
 * Uses the existing trending_rail pattern but for related content
 */
export const related_series = {
  id: 'related_series',
  name: 'Related Series',
  description: 'Similar series recommendations based on genre/tags',
  category: 'series',
  blockType: 'section_related_series',
  icon: 'sparkles',
  // Constraints
  constraints: {
    allowedLayouts: ['single'],
    maxBlocks: 6,
    requiredFields: [],
  },
  // Default props
  defaultProps: {
    heading: 'You Might Also Like',
    maxItems: 6,
    showViewAll: false,
    cardVariant: 'discovery',
  },
  layout: {
    preset: 'single',
    containerWidth: 'contained',
    padding: { top: 32, bottom: 48 },
  },
  background: {
    type: 'solid',
    color: 'transparent',
  },
};

/**
 * Creator Spotlight (Series Context) - Show the creator of this series
 */
export const series_creator_spotlight = {
  id: 'series_creator_spotlight',
  name: 'Series Creator',
  description: 'Spotlight on the creator of this series with follow button',
  category: 'series',
  blockType: 'section_series_creator',
  icon: 'user',
  // Constraints
  constraints: {
    allowedLayouts: ['single', 'split'],
    maxBlocks: 1,
    requiredFields: [],
  },
  // Default props
  defaultProps: {
    showAvatar: true,
    showBio: true,
    showFollowButton: true,
    showOtherWorks: true,
    maxOtherWorks: 3,
  },
  layout: {
    preset: 'single',
    containerWidth: 'contained',
    padding: { top: 24, bottom: 24 },
  },
  background: {
    type: 'solid',
    color: 'var(--color-surface-secondary)',
  },
};

/**
 * Series CTA - Tip/support/premium unlock section
 */
export const series_cta = {
  id: 'series_cta',
  name: 'Series Support CTA',
  description: 'Call-to-action for supporting the series (tips, premium)',
  category: 'series',
  blockType: 'section_series_cta',
  icon: 'heart',
  // Constraints
  constraints: {
    allowedLayouts: ['single', 'split'],
    maxBlocks: 1,
    requiredFields: [],
  },
  // Default props
  defaultProps: {
    heading: 'Support This Series',
    showTipButton: true,
    showShareButtons: true,
    showPremiumUnlock: true,
  },
  layout: {
    preset: 'single',
    containerWidth: 'contained',
    padding: { top: 24, bottom: 24 },
  },
  background: {
    type: 'gradient',
    gradient: { direction: 'to-r', from: 'var(--color-primary)', to: 'var(--color-primary-dark)' },
  },
};

export default {
  series_hero,
  episode_list,
  series_info_panel,
  series_comments,
  related_series,
  series_creator_spotlight,
  series_cta,
};
